import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, user, canAccess, logout } = useAuth();
  const location = useLocation();

  // Memoize the redirect state to prevent unnecessary re-renders
  const redirectState = useMemo(() => ({ from: location }), [location.pathname]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Only redirect if not already on a public route to prevent loops
    const publicRoutes = ['/', '/login', '/signup', '/auth/callback'];
    if (!publicRoutes.includes(location.pathname)) {
      return <Navigate to="/" state={redirectState} replace />;
    }
    // If already on a public route, don't render children
    return null;
  }

  // Force logout for suspended/deactivated users
  if (user && user.isActive === false) {
    logout();
    return <Navigate to="/" replace />;
  }

  if (requiredRole && user && !canAccess(requiredRole)) {
    // User doesn't have required role, redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;











